<script setup lang="ts">
  import { computed, watch } from 'vue'
  import type { AiPromptAsset, TargetPromptAiModel } from '../model/types'
  import { AI_PERMISSIONS } from '../constants'
  import { useUser } from '@gat-ship/user'
  import {
    useForm,
    z,
    Checkbox,
    FormField,
    InputGroupInput,
    InputGroupTextarea,
    SelectInput,
    type SelectInputItem
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'

  const props = defineProps<{
    asset: AiPromptAsset
    command: 'add' | 'update'
    baseInstructionAiModel?: string
    allAssets?: AiPromptAsset[]
    virtualAssets?: AiPromptAsset[]
    target?: AiPromptAsset['promptTargetId']
  }>()

  const emit = defineEmits<{
    (e: 'save', asset: AiPromptAsset): void
    (e: 'remove', asset: AiPromptAsset): void
  }>()

  const user = useUser()

  const isSystemPrompt = computed(() => props.asset.id === props.target)
  const isVirtual = computed(
    () => props.virtualAssets?.some((a) => a.id === props.asset.id) ?? false
  )
  const canSetDefault = computed(() => {
    if (props.asset.isDefault) return false
    const assets = props.allAssets ?? []
    if (isSystemPrompt.value) return assets.length > 1
    return assets.length > 0
  })
  const canRemove = computed(() => {
    return (
      !isVirtual.value &&
      props.command !== 'add' &&
      user.hasPermission(AI_PERMISSIONS.manageAssets)
    )
  })

  const schema = z
    .object({
      displayName: z.string().min(1, 'Display name is required'),
      shortDescription: z.string().min(1, 'Description is required'),
      promptText: z.string(),
      targetAiModel: z.string().min(1, 'Thinking model is required'),
      isDefault: z.boolean()
    })
    .superRefine((data, ctx) => {
      // System prompts already carry base instructions from the backend,
      // so the user-authored promptText is additive and may be left blank.
      if (
        !isSystemPrompt.value &&
        (!data.promptText || data.promptText.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['promptText'],
          message: 'Instructions are required'
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      displayName: props.asset.displayName ?? '',
      shortDescription: props.asset.shortDescription ?? '',
      promptText: props.asset.promptText ?? '',
      targetAiModel: props.asset.targetAiModel ?? 'default',
      isDefault: props.asset.isDefault ?? false
    } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      const updated: AiPromptAsset = {
        ...props.asset,
        displayName: value.displayName,
        shortDescription: value.shortDescription,
        promptText: value.promptText,
        targetAiModel: value.targetAiModel as TargetPromptAiModel,
        isDefault: value.isDefault
      }
      emit('save', updated)
    }
  })

  defineExpose({ form, canRemove, isSystemPrompt })

  watch(
    () => props.asset,
    (a) => {
      form.reset({
        displayName: a.displayName ?? '',
        shortDescription: a.shortDescription ?? '',
        promptText: a.promptText ?? '',
        targetAiModel: a.targetAiModel ?? 'default',
        isDefault: a.isDefault ?? false
      })
    },
    { deep: true }
  )

  const thinkingModelItems = computed<SelectInputItem<TargetPromptAiModel>[]>(
    () => {
      const items: SelectInputItem<TargetPromptAiModel>[] = []
      items.push({
        title: props.baseInstructionAiModel
          ? `Base Instruction (${props.baseInstructionAiModel})`
          : 'Default',
        value: 'default',
        metaData: 'default'
      })
      items.push({ title: 'Light', value: 'light', metaData: 'light' })
      items.push({ title: 'Standard', value: 'standard', metaData: 'standard' })
      items.push({ title: 'Extended', value: 'extended', metaData: 'extended' })
      return items
    }
  )
</script>

<template>
  <div class="flex flex-col gap-4">
    <FieldGroup>
      <FormField :form="form" name="displayName" label="Display Name" required>
        <template #default="{ field }">
          <InputGroupInput
            :id="field.name"
            :model-value="field.state.value ?? ''"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
      <FormField
        :form="form"
        name="shortDescription"
        label="Description"
        required
      >
        <template #default="{ field }">
          <InputGroupInput
            :id="field.name"
            :model-value="field.state.value ?? ''"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
      <FormField
        :form="form"
        name="promptText"
        :label="
          isSystemPrompt ? 'Additional Instructions (optional)' : 'Instructions'
        "
        :hint="`The instruction used by the AI model to extract the data. \n\nNote: This is in addition to the 'Base Instruction'.`"
        :required="!isSystemPrompt"
      >
        <template #default="{ field }">
          <InputGroupTextarea
            :id="field.name"
            :model-value="field.state.value ?? ''"
            class="max-h-[400px] min-h-[200px] w-full resize-none self-stretch overflow-y-auto overflow-x-hidden break-all"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
      <FormField
        :form="form"
        name="targetAiModel"
        label="Thinking Model"
        hint="The AI model used to run this prompt preset."
        required
      >
        <template #default="{ field, invalid }">
          <SelectInput
            :id="field.name"
            :model-value="field.state.value"
            :items="thinkingModelItems"
            :aria-invalid="invalid"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
      <component :is="form.Field" v-if="canSetDefault" name="isDefault">
        <template #default="{ field }">
          <label
            class="group/field flex cursor-pointer items-start gap-2 px-3 py-2"
          >
            <Checkbox
              :model-value="!!field.state.value"
              @update:model-value="
                (v) => {
                  field.handleChange(v === true)
                  field.handleBlur()
                }
              "
            />
            <span class="flex flex-col gap-1">
              <span
                class="text-xs font-medium leading-4 text-neutral-700 dark:text-neutral-200"
              >
                Make this the default prompt preset.
              </span>
              <span
                class="text-muted-foreground whitespace-pre-line text-xs leading-snug"
              >
                The default prompt preset will always be pre-selected
              </span>
            </span>
          </label>
        </template>
      </component>
    </FieldGroup>
  </div>
</template>
